// components/AnnouncementBanner.vue
<template>
  <div class="announcement-container" v-if="announcement">
    <div class="announcement-banner" :class="[`type-${announcement.type || 'info'}`, { 'has-close': showCloseButton }]">
      <div class="announcement-content">
        <div class="icon-wrapper" v-if="announcement.type">
          <!-- Info Icon -->
          <svg v-if="announcement.type === 'info'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          
          <!-- Success Icon -->
          <svg v-if="announcement.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          
          <!-- Warning Icon -->
          <svg v-if="announcement.type === 'warning'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          
          <!-- Error Icon -->
          <svg v-if="announcement.type === 'error'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          
          <!-- Custom Icon (if type is not standard) -->
          <slot name="icon" v-if="!['info', 'success', 'warning', 'error'].includes(announcement.type)">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </slot>
        </div>
        <span class="message">{{ announcement.message }}</span>
      </div>
      <button 
        v-if="showCloseButton" 
        class="close-button"
        @click="closeAnnouncement"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6L6 18"></path>
          <path d="M6 6l12 12"></path>
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue'

const props = defineProps({
  announcement: {
    type: Object,
    default: () => ({
      message: 'Earn points and redeem any drop you want.',
      type: 'info'
    })
  },
  showCloseButton: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close'])

const closeAnnouncement = () => {
  emit('close')
}
</script>

<style scoped>
.announcement-container {
  margin: 16px 0;
  padding: 0 16px;
}

.announcement-banner {
  background-color: rgba(61, 54, 84, 0.5);
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.announcement-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a8a4b4;
}

.message {
  color: #a8a4b4;
  font-size: 14px;
  line-height: 1.4;
}

.close-button {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #a8a4b4;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

/* 不同类型的公告样式 */
.announcement-banner.type-info {
  background-color: rgba(61, 54, 84, 0.5);
}

.announcement-banner.type-success {
  background-color: rgba(34, 197, 94, 0.1);
  border-color: rgba(34, 197, 94, 0.2);
}

.announcement-banner.type-success .message,
.announcement-banner.type-success .icon-wrapper {
  color: #22c55e;
}

.announcement-banner.type-warning {
  background-color: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.2);
}

.announcement-banner.type-warning .message,
.announcement-banner.type-warning .icon-wrapper {
  color: #f59e0b;
}

.announcement-banner.type-error {
  background-color: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.2);
}

.announcement-banner.type-error .message,
.announcement-banner.type-error .icon-wrapper {
  color: #ef4444;
}
</style>